import { StyledRow } from '@/components';
import { Col, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styles from './PersonalizationSummary.module.scss';
import { PersonalizationTableRowValues } from './PersonalizationDetails';
import { formatters } from '@/core';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { PersonalizationSummaryHomeTotalBalance } from './PersonalizationSummaryHomeTotalBalance';

interface Props {
  allPersonalizations: PersonalizationTableRowValues[];
  className?: string;
  showBaseCost: boolean;
  showResidentPrice: boolean;
}

const Title = Typography.Title;

function groupTaxes(allPersonalizations: PersonalizationTableRowValues[]) {
  return Object.values(
    allPersonalizations
      .filter(
        (x) =>
          (x.personalizationItem?.pricing?.residentTax != null &&
            x.personalizationItem.pricing.residentTaxPrice != null) ||
          (x.specialRequest?.prices?.residentTax != null &&
            x.specialRequest.prices.residentTaxPrice != null),
      )
      .reduce<Record<string, { residentSalesTax: number; taxAmount: number }>>(
        (acc, curr) => {
          const residentSalesTax =
            curr.personalizationItem?.pricing?.residentTax ??
            curr.specialRequest?.prices?.residentTax!;
          const taxKey = residentSalesTax.toString();

          if (!acc[taxKey]) {
            acc[taxKey] = {
              residentSalesTax,
              taxAmount:
                curr.personalizationItem?.pricing?.residentTaxPrice ??
                curr.specialRequest?.prices?.residentTaxPrice!,
            };
          } else {
            acc[taxKey].taxAmount +=
              curr.personalizationItem?.pricing?.residentTaxPrice ??
              curr.specialRequest?.prices?.residentTaxPrice!;
          }
          return acc;
        },
        {},
      ),
  );
}

export function PersonalizationSummarySalesTax(props: Props) {
  const { allPersonalizations, className, showBaseCost, showResidentPrice } =
    props;
  const { t } = useTranslation(undefined, {
    keyPrefix: 'personalization.summary',
  });
  const grouped = groupTaxes(allPersonalizations);

  return (
    <>
      {!isEmpty(allPersonalizations) && !isEmpty(grouped) && (
        <PersonalizationSummaryHomeTotalBalance
          allPersonalizations={allPersonalizations}
          showBaseCost={showBaseCost}
          showResidentPrice={showResidentPrice}
          preTax
        />
      )}
      <div className={className}>
        {grouped
          .filter((x) => x.residentSalesTax && x.residentSalesTax > 0)
          .sort((a, b) => a.residentSalesTax - b.residentSalesTax)
          .map((x) => (
            <StyledRow
              className={classNames(styles.totalInfo, 'mb-1')}
              key={x.residentSalesTax}
            >
              <Col span={19}>
                <Title
                  className={classNames(styles.totalTitle, styles.taxTitle)}
                  level={4}
                >
                  {t('tax')}{' '}
                  {formatters.truncateToPercent(x.residentSalesTax, 2)}
                  {'%'}
                </Title>
              </Col>
              <Col span={4}>
                <Title level={3} className={styles.totalValue}>
                  {'$'}
                  {formatters.price(x.taxAmount)}
                </Title>
              </Col>
            </StyledRow>
          ))}
      </div>
    </>
  );
}
